import { FC, useEffect, useState } from "react";

// Componentss
import Slider from "../../../../components/forms/Slider";
import MoneyInput from "../forms/MoneyInput";
import YearMonthDisplay from "../forms/YearMonthDisplay";

// Services
import { DRFlowResolver } from "services/DRSolverService";
import { numPrettier, getYearMonth } from "services/TextPrettierService";

// Types
import { DRSliderSettingsOutput } from "types/DRInterface";

// Calculator variables
const loanAmountLimit = { step: 100 };
const monthlyPaymentLimit = { step: 1 };

const LoanCalculatorInputBox: FC<{
  getCalculatorInputValues: any;
}> = ({ getCalculatorInputValues }) => {
  const [sliderSettings, setSliderSettings] = useState<DRSliderSettingsOutput>({
    loanMin: 0,
    loanDefault: 0,
    loanMax: 0,
    repaymentMonthsMin: 0,
    repaymentMonthsMax: 0,
  });

  const [loanAmount, setLoanAmount] = useState<number>(0);
  const [loanDuration, setLoanDuration] = useState<number>(
    sliderSettings.repaymentMonthsMin
  );

  const [slidersLoading, setSlidersLoading] = useState(true);

  // ? Get slider settings from DR
  const getLoanSliderSettings = () => {
    DRFlowResolver("75f96235-f8ea-32ab-2833-3494030f5da6", {
      loanAmount: loanAmount === 0 ? null : loanAmount,
    })
      .then((response) => {
        if (loanAmount === 0) {
          setLoanAmount(response[0].loanDefault);
        }
        setSliderSettings(response[0]);

        setSlidersLoading(false);
      })
      .catch((Err) => console.error("Sliders fetch error: ", Err));
  };

  // ?  Get slider settings from DR every 50ms on change
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      getLoanSliderSettings();
    }, 50);

    return () => clearTimeout(delayDebounceFn);
  }, [loanAmount]);

  useEffect(() => {
    getCalculatorInputValues(loanAmount, loanDuration);
  }, [loanAmount, loanDuration]);

  // ? Slider values validation
  useEffect(() => {
    if (loanAmount < sliderSettings.loanMin) {
      setLoanAmount(sliderSettings.loanMin);
    } else if (loanAmount > sliderSettings.loanMax) {
      setLoanAmount(sliderSettings.loanMax);
    }
    if (loanDuration < sliderSettings.repaymentMonthsMin) {
      setLoanDuration(sliderSettings.repaymentMonthsMin);
    } else if (loanDuration > sliderSettings.repaymentMonthsMax) {
      setLoanDuration(sliderSettings.repaymentMonthsMax);
    }
  }, [sliderSettings]);

  return (
    <>
      {slidersLoading ? (
        <div className="loan-calculator__box loan-calculator__input-box">
          <div className="input-section">
            <div className="line line-40"></div>
            <div className="slider-loader">
              <div className="line"></div>
              <div className="line"></div>
            </div>
          </div>
          <div className="input-section">
            <div className="line line-40"></div>
            <div className="slider-loader">
              <div className="line"></div>
              <div className="line"></div>
            </div>
          </div>
        </div>
      ) : (
        <div className="loan-calculator__box loan-calculator__input-box">
          <div className="input-section">
            <p className="slider--label text text--primary fw--500">
              How much do you plan to borrow?
            </p>
            <div className="input-section__slider">
              <Slider
                name="loanAmountSlider"
                label="Loan Amount"
                min={sliderSettings.loanMin}
                max={sliderSettings.loanMax}
                minLabel={`$${numPrettier(sliderSettings.loanMin)}`}
                maxLabel={`$${numPrettier(sliderSettings.loanMax)}`}
                step={loanAmountLimit.step}
                value={loanAmount}
                getValueOnChange={(value: number) => {
                  setLoanAmount(value);
                }}
              />
              <MoneyInput
                name="loan-amount"
                label="Loan amount"
                min={sliderSettings.loanMin}
                max={sliderSettings.loanMax}
                step={loanAmountLimit.step}
                value={loanAmount}
                getValueOnChange={(targetValue: any) => {
                  setLoanAmount(targetValue);
                }}
              />
            </div>
          </div>
          <div className="input-section">
            <p className="slider--label text text--primary fw--500">
              How long do you want to repay?
            </p>
            <div className="input-section__slider">
              <Slider
                name="monthlyPaymentSlider"
                label="Monthly payment"
                min={sliderSettings.repaymentMonthsMin}
                max={sliderSettings.repaymentMonthsMax}
                minLabel={getYearMonth(sliderSettings.repaymentMonthsMin)}
                maxLabel={getYearMonth(sliderSettings.repaymentMonthsMax)}
                step={monthlyPaymentLimit.step}
                value={loanDuration}
                getValueOnChange={(value: number) => {
                  setLoanDuration(value);
                }}
              />
              <YearMonthDisplay months={loanDuration} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LoanCalculatorInputBox;
